/**
 * `CompetencyMatchCard` — card di una competenza nella Dashboard.
 *
 * Design ref: "Componenti chiave → Dashboard": lista di card ordinate per
 * `rankScore` (usato solo per l'ordinamento, non mostrato). La card distingue
 * due metriche complementari:
 *  - `coveragePercent` (0..100) = **Copertura** (ampiezza: quante skill
 *    richieste l'utente possiede), come donut nell'angolo in alto a destra
 *    (card piena) o donut a inizio riga (variante compatta);
 *  - `proficiencyPercent` (0..100 o `null`) = **Proficiency** (profondità:
 *    quanto è bravo sulle skill che possiede), come barra di progresso;
 *  - in un footer ancorato in fondo: "N/M skill al livello richiesto" a sinistra e il chip
 *    della business unit a destra (card piena); nella variante compatta il chip
 *    resta accanto al nome.
 *
 * La card è puramente presentazionale: riceve un `CompetencyMatch` e lo
 * visualizza. *Validates: 9.4, 9.5, 9.6.*
 */
import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CompetencyMatch } from '../shared/dto';
import { scoreColor } from './StarRating';

interface CompetencyMatchCardProps {
  match: CompetencyMatch;
  /**
   * Variante compatta a riga orizzontale (usata per le competenze "altre" nella
   * Dashboard, sotto le competenze in evidenza). Default: `false` (card piena).
   */
  compact?: boolean;
  /**
   * Variante "hero": la competenza principale (match più elevato) mostrata in
   * grande in cima alla Dashboard, con un badge che la identifica. Ha la
   * precedenza su `compact`. Default: `false`.
   */
  hero?: boolean;
  /**
   * Costruisce il path di dettaglio della competenza a partire dal suo id. Serve
   * a riusare la card sia nella Dashboard personale (`/competences/:id`) sia nel
   * drill-down del Team (`/team/:userId/competences/:id`). Se assente, si usa il
   * path della dashboard personale.
   */
  detailPath?: (competenceId: string) => string;
  /**
   * Se `true` (default) la card è navigazionale: cliccabile e attivabile da
   * tastiera (apre il dettaglio della competenza). Con `false` la card è
   * puramente presentazionale (nessun `role`/`tabIndex`/handler né classe
   * `--interactive`), usata per i profili esterni non navigabili.
   */
  interactive?: boolean;
}

/** Formatta una percentuale `0..100` come intero con `%`. */
function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

/** Raggio del cerchio del donut (viewBox 36×36, stroke 4 → centro a 18). */
const DONUT_RADIUS = 16;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/**
 * `ScoreDonut` — anello di progresso che rappresenta una percentuale (0..100).
 * Usato per la **Copertura** della competenza. L'anello è riempito in
 * proporzione al valore e colorato con la stessa scala del match (rosso→verde);
 * al centro il valore intero. `label` personalizza il testo accessibile.
 */
function ScoreDonut({ score, label }: { score: number; label: string }) {
  // Il valore può superare 100 (bonus certificazioni): l'anello si riempie al
  // massimo (100%) e assume il trattamento oro; il centro mostra "100+".
  const isOverMax = score > 100;
  const clamped = Math.min(100, Math.max(0, score));
  const rounded = Math.round(clamped);
  const exact = Math.round(Math.max(0, score));
  // Colore su scala 0..10 (come il match); oltre 100 usa l'oro; fallback brand.
  const ringColor = isOverMax
    ? 'var(--color-gold, #d4a017)'
    : scoreColor(clamped / 10) ?? 'var(--color-primary)';
  // Porzione di circonferenza da riempire (il resto resta "track").
  const dash = (clamped / 100) * DONUT_CIRCUMFERENCE;

  return (
    <div
      className={[
        'competency-match-card__donut',
        isOverMax ? 'competency-match-card__donut--gold' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="img"
      aria-label={isOverMax ? `${label} oltre 100 (${exact})` : `${label} ${rounded} su 100`}
      title={isOverMax ? `${label} ${exact}/100` : `${label} ${rounded}/100`}
    >
      <svg viewBox="0 0 36 36" className="competency-match-card__donut-svg" aria-hidden="true">
        <circle className="competency-match-card__donut-track" cx="18" cy="18" r={DONUT_RADIUS} />
        <circle
          className="competency-match-card__donut-fill"
          cx="18"
          cy="18"
          r={DONUT_RADIUS}
          stroke={ringColor}
          strokeDasharray={`${dash} ${DONUT_CIRCUMFERENCE - dash}`}
          // Parte dall'alto (ore 12) invece che da ore 3.
          transform="rotate(-90 18 18)"
        />
      </svg>
      <span className="competency-match-card__donut-value" aria-hidden="true">
        {isOverMax ? '100+' : rounded}
      </span>
    </div>
  );
}

/**
 * `MetricBar` — barra di progresso etichettata per una percentuale `0..100`.
 * Colorata con la scala rosso→verde del match. Se `value` è `null` (metrica non
 * misurabile, es. proficiency senza skill valutate) mostra un trattino "—".
 */
function MetricBar({ label, value }: { label: string; value: number | null }) {
  if (value === null) {
    return (
      <div className="competency-match-card__metric">
        <dt>{label}</dt>
        <dd>
          <span
            className="competency-match-card__value competency-match-card__value--empty"
            aria-label={`${label} non disponibile`}
          >
            —
          </span>
        </dd>
      </div>
    );
  }

  // Il valore può superare 100 (bonus certificazioni): la barra si riempie al
  // massimo e assume il trattamento oro; l'etichetta mostra "100%+".
  const isOverMax = value > 100;
  const clamped = Math.min(100, Math.max(0, value));
  const exact = Math.round(Math.max(0, value));
  const color = scoreColor(clamped / 10);
  const displayText = isOverMax ? '100%+' : formatPercent(clamped);

  return (
    <div className="competency-match-card__metric">
      <dt>{label}</dt>
      <dd>
        <span
          role="progressbar"
          aria-valuenow={exact}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={isOverMax ? `${label} oltre 100% (${exact}%)` : `${label} ${displayText}`}
          className="competency-match-card__bar"
        >
          <span
            className={[
              'competency-match-card__bar-fill',
              isOverMax ? 'competency-match-card__bar-fill--gold' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              width: `${clamped}%`,
              ...(!isOverMax && color ? { background: color } : {}),
            }}
          />
        </span>
        <span
          className={[
            'competency-match-card__value',
            isOverMax ? 'competency-match-card__value--gold' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {displayText}
        </span>
      </dd>
    </div>
  );
}

export function CompetencyMatchCard({
  match,
  compact = false,
  hero = false,
  detailPath,
  interactive = true,
}: CompetencyMatchCardProps) {
  const navigate = useNavigate();
  const { coveragePercent, proficiencyPercent, evaluatedSkillCount, totalSkillCount } = match;

  // La card è cliccabile: apre la pagina di dettaglio della competenza. Resta
  // raggiungibile da tastiera (Invio/Spazio) grazie a `role="button"`.
  const openDetail = () => {
    const path = detailPath
      ? detailPath(match.competenceId)
      : `/competences/${encodeURIComponent(match.competenceId)}`;
    void navigate(path);
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDetail();
    }
  };
  // Props di interazione applicate solo quando `interactive` è `true`: così la
  // navigazione (openDetail) non viene mai attivata sulle card statiche.
  const interactiveProps = interactive
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: openDetail,
        onKeyDown: handleKeyDown,
        'aria-label': `Apri il dettaglio della competenza ${match.name}`,
      }
    : {};

  const departmentChip = match.departmentName !== null && (
    <span
      className="competency-match-card__chip"
      aria-label={`Business unit ${match.departmentName}`}
    >
      {match.departmentName}
    </span>
  );

  // "N/M skill al livello richiesto": segnale di quante skill soddisfano il
  // Livello_Richiesto della competenza (le skill sotto soglia hanno score=null e
  // non entrano nel numeratore), mostrato in piccolo.
  const skillsMeta = (
    <span
      className="competency-match-card__skills"
      aria-label={`${evaluatedSkillCount} di ${totalSkillCount} skill al livello richiesto`}
    >
      {evaluatedSkillCount}/{totalSkillCount} skill al livello richiesto
    </span>
  );

  // Variante "hero": la competenza principale, mostrata in grande in cima alla
  // Dashboard. Stesso contenuto della card piena ma con un badge identificativo,
  // donut e nome più grandi (le dimensioni sono gestite via CSS).
  if (hero) {
    return (
      <article
        className={[
          'competency-match-card',
          'competency-match-card--hero',
          interactive ? 'competency-match-card--interactive' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        {...interactiveProps}
        {...(interactive
          ? { 'aria-label': `Apri il dettaglio della competenza principale ${match.name}` }
          : {})}
      >
        {/* Donut della Copertura (ampiezza) nell'angolo in alto a destra. */}
        <ScoreDonut score={coveragePercent} label="Copertura" />

        <div className="competency-match-card__header">
          <span className="competency-match-card__badge">Competenza principale</span>
          <h3 className="competency-match-card__name">{match.name}</h3>
        </div>
        {/* Descrizione e missione della competenza: testi liberi facoltativi,
            mostrati solo nella hero e solo se valorizzati. */}
        {match.description && (
          <p className="competency-match-card__description">{match.description}</p>
        )}
        {match.mission && (
          <p className="competency-match-card__mission">
            <span className="competency-match-card__mission-label">Mission</span>
            {match.mission}
          </p>
        )}
        <dl className="competency-match-card__metrics">
          <MetricBar label="Proficiency" value={proficiencyPercent} />
        </dl>
        {/* Footer ancorato in fondo: skill valutate a sinistra, business unit a
            destra. */}
        <div className="competency-match-card__footer">
          {skillsMeta}
          {departmentChip}
        </div>
      </article>
    );
  }

  // Variante compatta: riga orizzontale con il donut della Copertura a sinistra,
  // nome/chip e "N/M skill" al centro, e la Proficiency (barra + %) a destra.
  if (compact) {
    return (
      <article
        className={[
          'competency-match-card',
          'competency-match-card--compact',
          interactive ? 'competency-match-card--interactive' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        {...interactiveProps}
      >
        <ScoreDonut score={coveragePercent} label="Copertura" />
        <div className="competency-match-card__main">
          <div className="competency-match-card__title">
            <h3 className="competency-match-card__name">{match.name}</h3>
            {departmentChip}
          </div>
          {skillsMeta}
        </div>
        <dl className="competency-match-card__inline-metrics">
          <MetricBar label="Proficiency" value={proficiencyPercent} />
        </dl>
      </article>
    );
  }

  return (
    <article
      className={[
        'competency-match-card',
        interactive ? 'competency-match-card--interactive' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      {...interactiveProps}
    >
      {/* Donut della Copertura (ampiezza) nell'angolo in alto a destra. */}
      <ScoreDonut score={coveragePercent} label="Copertura" />

      <div className="competency-match-card__header">
        <h3 className="competency-match-card__name">{match.name}</h3>
      </div>
      <dl className="competency-match-card__metrics">
        {/* La Copertura è già rappresentata dal donut in alto a destra: qui
            mostriamo solo la Proficiency per non duplicare l'informazione. */}
        <MetricBar label="Proficiency" value={proficiencyPercent} />
      </dl>
      {/* Footer ancorato in fondo: skill valutate a sinistra, business unit a
          destra. */}
      <div className="competency-match-card__footer">
        {skillsMeta}
        {departmentChip}
      </div>
    </article>
  );
}
